import { jsxs, jsx } from "react/jsx-runtime";
import { useState, useMemo, useCallback, useEffect } from "react";
import { KeyboardArrowUp, KeyboardArrowDown } from "@mui/icons-material";
import { Menu, Popover, Typography, FormGroup, Switch as Switch$1, FormControlLabel, Checkbox, Divider } from "@mui/material";
import { TodoDataTestIds } from "../../constants/dataTestIds.js";
import { getOriginTitles } from "../../helpers/todo.js";
import { useLocalStorage } from "../../hooks/useLocalStorage.js";
import { useFilters } from "../../providers/FilterProvider.js";
import { useTodoListProvider } from "../../providers/TodoListProvider.js";
import { Origin, SortingDirection } from "../../todo-list.types.js";
import { Container, SortButton, SortMenuItem, SliderFilterIcon, Counter, Switch } from "./FilterSorter.styles.js";
import "@mui/material/styles";
import "../../assets/icons/SliderFilter.js";
const FilterSorter = ({ onSort, onFilter }) => {
  const { labels } = useTodoListProvider();
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [isTaskTypesSwitchOn, setIsTaskTypesSwitchOn] = useState(false);
  const [isCategoryTypesSwitchOn, setIsCategoryTypesSwitchOn] = useState(false);
  const [areCategoryFiltersEnabled, setAreCategoryFiltersEnabled] = useState(true);
  const categoryFilters = useMemo(() => Object.values(Origin), []);
  const defaultFilter = useMemo(() => ["todo", "done", ...categoryFilters], [categoryFilters]);
  const [sortFilter, setSortFilter] = useLocalStorage("sortConfig", {
    sortBy: "relevance",
    order: SortingDirection.ASC,
    filter: defaultFilter
  });
  const { setFilters, filters } = useFilters();
  const originLabels = useMemo(() => getOriginTitles(labels), [labels]);
  const activeFiltersCount = useMemo(
    () => defaultFilter.filter((data) => data !== Origin.MANUAL && filters[data]).length,
    [filters]
  );
  const isSortOpen = Boolean(sortAnchorEl);
  const isFilterOpen = Boolean(filterAnchorEl);
  const [selectedLabel, setSelectedLabel] = useState(labels == null ? void 0 : labels.sort.relevance);
  const [filter, setFilter] = useState((sortFilter == null ? void 0 : sortFilter.filter) || []);
  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };
  const handleSortClose = () => {
    setSortAnchorEl(null);
  };
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };
  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };
  const handleSortChange = useCallback(
    (sortBy, order = SortingDirection.DESC, label) => () => {
      setSortFilter({ ...sortFilter, sortBy, order });
      setSelectedLabel(label);
      onSort(sortBy, order);
      handleSortClose();
    },
    [onSort, setSortFilter, sortFilter]
  );
  const updateSortFilter = useCallback(
    (newFilter) => {
      setSortFilter((prevSortFilter) => {
        const updatedSortFilter = {
          sortBy: (prevSortFilter == null ? void 0 : prevSortFilter.sortBy) || "relevance",
          order: (prevSortFilter == null ? void 0 : prevSortFilter.order) || SortingDirection.DESC,
          filter: newFilter
        };
        return updatedSortFilter;
      });
    },
    [setSortFilter]
  );
  const handleFilterChange = useCallback(
    (filterType) => (_e, checked) => {
      setFilter((prevFilter) => {
        const newFilter = prevFilter.includes(filterType) ? prevFilter.filter((f) => f !== filterType) : [...prevFilter, filterType];
        setIsTaskTypesSwitchOn(newFilter.includes("todo") && newFilter.includes("done"));
        setIsCategoryTypesSwitchOn(
          categoryFilters.every((currentFilter) => {
            var _a;
            return (_a = sortFilter == null ? void 0 : sortFilter.filter) == null ? void 0 : _a.includes(currentFilter);
          })
        );
        setSortFilter((prevSortFilter) => {
          const defaultSortFilter = {
            sortBy: "relevance",
            order: SortingDirection.ASC,
            filter: defaultFilter
          };
          return {
            ...defaultSortFilter,
            ...prevSortFilter,
            filter: newFilter
          };
        });
        setFilters((prev) => ({
          ...prev,
          [filterType]: checked
        }));
        return newFilter;
      });
      onFilter();
    },
    [setFilter, setSortFilter, setFilters, defaultFilter, categoryFilters, sortFilter == null ? void 0 : sortFilter.filter]
  );
  const handleTaskTypesSwitchChange = useCallback(
    (event) => {
      const { checked } = event.target;
      setIsTaskTypesSwitchOn(checked);
      const newFilter = checked ? Array.from(/* @__PURE__ */ new Set([...filter, "todo", "done"])) : filter.filter((f) => f !== "todo" && f !== "done");
      setFilter(newFilter);
      updateSortFilter(newFilter);
      setFilters((prev) => ({
        ...prev,
        todo: checked,
        done: checked,
        recall: checked
      }));
    },
    [setFilter, setFilters, updateSortFilter, filter]
  );
  const handleCategoryTypesSwitchChange = useCallback(
    (event) => {
      const { checked } = event.target;
      setIsCategoryTypesSwitchOn(checked);
      const taskFilters = filter.filter((f) => f === "todo" || f === "done");
      let newFilter;
      if (checked) {
        newFilter = Array.from(/* @__PURE__ */ new Set([...taskFilters, ...categoryFilters]));
      } else {
        const nonCategoryFilters = filter.filter((f) => !categoryFilters.includes(f));
        newFilter = [...taskFilters, ...nonCategoryFilters];
      }
      setFilter(newFilter);
      updateSortFilter(newFilter);
      setFilters((prev) => ({
        ...prev,
        ...Object.values(Origin).reduce(
          (acc, key) => ({
            ...acc,
            [key]: key === Origin.MANUAL ? false : checked
          }),
          {}
        )
      }));
    },
    [setFilter, updateSortFilter, setFilters, filter, categoryFilters]
  );
  useEffect(() => {
    const initialLabel = (
      // eslint-disable-next-line no-nested-ternary
      (sortFilter == null ? void 0 : sortFilter.sortBy) === "relevance" ? labels == null ? void 0 : labels.sort.relevance : (sortFilter == null ? void 0 : sortFilter.order) === SortingDirection.DESC ? labels == null ? void 0 : labels.sort.newest : labels == null ? void 0 : labels.sort.oldest
    );
    setSelectedLabel(initialLabel);
  }, [sortFilter, labels == null ? void 0 : labels.sort.newest, labels == null ? void 0 : labels.sort.oldest, labels == null ? void 0 : labels.sort.relevance]);
  useEffect(() => {
    var _a, _b;
    setIsTaskTypesSwitchOn(!!(((_a = sortFilter == null ? void 0 : sortFilter.filter) == null ? void 0 : _a.includes("todo")) && ((_b = sortFilter == null ? void 0 : sortFilter.filter) == null ? void 0 : _b.includes("done"))));
    const areBothUnchecked = !filter.includes("todo") && !filter.includes("done");
    setIsCategoryTypesSwitchOn(!areBothUnchecked && isCategoryTypesSwitchOn);
    setAreCategoryFiltersEnabled(!areBothUnchecked);
  }, [sortFilter == null ? void 0 : sortFilter.filter, isCategoryTypesSwitchOn, filter]);
  useEffect(() => {
    setIsCategoryTypesSwitchOn(categoryFilters.every((currentFilter) => {
      var _a;
      return (_a = sortFilter == null ? void 0 : sortFilter.filter) == null ? void 0 : _a.includes(currentFilter);
    }));
  }, [sortFilter == null ? void 0 : sortFilter.filter, categoryFilters]);
  return /* @__PURE__ */ jsxs(Container, { "data-testid": TodoDataTestIds.TodoStatusContainer, children: [
    /* @__PURE__ */ jsx(
      SortButton,
      {
        "aria-haspopup": "true",
        onClick: handleSortClick,
        disableRipple: true,
        fullWidth: true,
        disableFocusRipple: true,
        disableTouchRipple: true,
        endIcon: isSortOpen ? /* @__PURE__ */ jsx(KeyboardArrowUp, {}) : /* @__PURE__ */ jsx(KeyboardArrowDown, {}),
        sx: {
          "&:hover": {
            backgroundColor: "white"
          }
        },
        children: selectedLabel
      }
    ),
    /* @__PURE__ */ jsxs(
      Menu,
      {
        id: "sort-menu",
        anchorEl: sortAnchorEl,
        keepMounted: true,
        open: isSortOpen,
        onClose: handleSortClose,
        slotProps: {
          paper: {
            style: {
              width: sortAnchorEl == null ? void 0 : sortAnchorEl.offsetWidth
            }
          }
        },
        children: [
          /* @__PURE__ */ jsx(
            SortMenuItem,
            {
              onClick: handleSortChange("relevance", SortingDirection.ASC, labels == null ? void 0 : labels.sort.relevance),
              selected: (sortFilter == null ? void 0 : sortFilter.sortBy) === "relevance",
              children: labels == null ? void 0 : labels.sort.relevance
            }
          ),
          /* @__PURE__ */ jsx(
            SortMenuItem,
            {
              onClick: handleSortChange("createdAt", SortingDirection.DESC, labels == null ? void 0 : labels.sort.newest),
              selected: (sortFilter == null ? void 0 : sortFilter.sortBy) === "createdAt" && (sortFilter == null ? void 0 : sortFilter.order) === SortingDirection.DESC,
              children: labels == null ? void 0 : labels.sort.newest
            }
          ),
          /* @__PURE__ */ jsx(
            SortMenuItem,
            {
              onClick: handleSortChange("createdAt", SortingDirection.ASC, labels == null ? void 0 : labels.sort.oldest),
              selected: (sortFilter == null ? void 0 : sortFilter.sortBy) === "createdAt" && (sortFilter == null ? void 0 : sortFilter.order) === SortingDirection.ASC,
              children: labels == null ? void 0 : labels.sort.oldest
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsxs(
      SortButton,
      {
        "aria-haspopup": "true",
        onClick: handleFilterClick,
        endIcon: isFilterOpen ? /* @__PURE__ */ jsx(KeyboardArrowUp, {}) : /* @__PURE__ */ jsx(KeyboardArrowDown, {}),
        disableRipple: true,
        disableFocusRipple: true,
        disableTouchRipple: true,
        filters: true,
        sx: {
          "&:hover": {
            backgroundColor: "white"
          }
        },
        children: [
          /* @__PURE__ */ jsx(SliderFilterIcon, {}),
          /* @__PURE__ */ jsx(Counter, { children: activeFiltersCount })
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      Popover,
      {
        id: "filter-menu",
        open: isFilterOpen,
        anchorEl: filterAnchorEl,
        onClose: handleFilterClose,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "left"
        },
        children: /* @__PURE__ */ jsxs(Typography, { sx: { p: 2 }, children: [
          /* @__PURE__ */ jsxs(FormGroup, { children: [
            /* @__PURE__ */ jsxs(Switch, { children: [
              /* @__PURE__ */ jsx(Typography, { children: labels == null ? void 0 : labels.filter.taskTypes }),
              /* @__PURE__ */ jsx(Switch$1, { checked: isTaskTypesSwitchOn, onChange: handleTaskTypesSwitchChange })
            ] }),
            /* @__PURE__ */ jsx(
              FormControlLabel,
              {
                control: /* @__PURE__ */ jsx(Checkbox, { onChange: handleFilterChange("todo"), checked: filter.includes("todo") }),
                label: labels == null ? void 0 : labels.openTodos
              }
            ),
            /* @__PURE__ */ jsx(
              FormControlLabel,
              {
                control: /* @__PURE__ */ jsx(Checkbox, { onChange: handleFilterChange("done"), checked: filter.includes("done") }),
                label: labels == null ? void 0 : labels.doneTodos
              }
            )
          ] }),
          /* @__PURE__ */ jsx(Divider, { sx: { my: 2 } }),
          /* @__PURE__ */ jsxs(FormGroup, { children: [
            /* @__PURE__ */ jsxs(Switch, { children: [
              /* @__PURE__ */ jsx(Typography, { children: labels.filter.appointmentTypes }),
              /* @__PURE__ */ jsx(
                Switch$1,
                {
                  checked: isCategoryTypesSwitchOn,
                  onChange: handleCategoryTypesSwitchChange,
                  disabled: !areCategoryFiltersEnabled
                }
              )
            ] }),
            Object.entries(originLabels).filter(([key]) => key !== Origin.MANUAL).map(([key, value]) => /* @__PURE__ */ jsx(
              FormControlLabel,
              {
                control: /* @__PURE__ */ jsx(
                  Checkbox,
                  {
                    onChange: handleFilterChange(key),
                    checked: filter.includes(key),
                    disabled: !areCategoryFiltersEnabled
                  }
                ),
                label: value
              },
              key
            ))
          ] })
        ] })
      }
    )
  ] });
};
export {
  FilterSorter as default
};
